(function () {
  'use strict';
  if (customElements.get('mip-header-currency')) return;
  const preferenceKey = 'mip_display_currency_v1';
  const priceSelector = '.pmx-product-price,.feb__card-price,.custom-price-row,.money,.price,.price-item,.pc-price__regular,.pc-price__sale,.pc-price__compare,.pc-price__save,.unit-price,.cart-item-value,.cart-total-value,.previewCartItem-price,.previewCartTotals-value,[data-item-original-price-display],[data-item-final-price-display],[data-item-price-with-quantity-display],[data-original-price-display],[data-current-price-display],[data-mip-money]';
  const ignoredSelector = 'script,style,svg,textarea,select,option,input,label,price-range,.facets,.facets__price,.price-range,.sidebarBlock,.mip-top-filters,.shopify-payment-button,shopify-payment-terms,[data-payment-terms],[data-mip-currency-ignore],.mip-region,.mip-display-notice,.mip-conversion-note';
  const escapePattern = value => value.replace(/[.*+?^$}{()|[\]\\]/g, '\\$&');
  // Currency display punctuation may differ from Shopify's base money format.
  // Determine the decimal separator from each amount (199,95 / 199.95), not
  // from a separate formatting sample, to avoid 100x inflated conversions.
  const parseMoneyAmount = (raw, fractionDigits = 2) => {
    const plain = raw.replace(/[\s\u00a0\u202f'’]/g, '');
    if (!/^[+-]?\d[\d.,]*$/.test(plain)) return NaN;
    const last = Math.max(plain.lastIndexOf('.'), plain.lastIndexOf(','));
    const decimalPlaces = last < 0 ? 0 : plain.length - last - 1;
    const hasFraction = last >= 0 && decimalPlaces > 0 && decimalPlaces <= Math.max(2, fractionDigits);
    const normalized = hasFraction
      ? plain.slice(0, last).replace(/[.,]/g, '') + '.' + plain.slice(last + 1)
      : plain.replace(/[.,]/g, '');
    return Number(normalized);
  };

  let converter;

  class DisplayCurrency {
    constructor(config) {
      this.config = config;
      this.base = config.baseCurrency;
      this.selected = this.base;
      this.pending = new Set();
      this.accountPage = /\/(?:account|orders)(?:\/|$)|\/pages\/my-moda(?:\/|$)/.test(location.pathname);
      this.fractionDigits = new Intl.NumberFormat('en', { style: 'currency', currency: this.base }).resolvedOptions().maximumFractionDigits;
      const tokens = [...new Set([this.base, config.baseSymbol].filter(Boolean))]
        .sort((a, b) => b.length - a.length).map(escapePattern).join('|');
      const amount = "[-+]?\\d(?:[\\d.,\\u00a0\\u202f '’]*\\d)?";
      this.moneyPattern = new RegExp('(?:(?:' + tokens + ')\\s*(' + amount + ')(?:\\s*' + escapePattern(this.base) + ')?|(' + amount + ')\\s*(?:' + tokens + '))', 'g');
      this.observer = new MutationObserver(records => {
        for (const record of records) {
          if (record.type === 'characterData') this.queue(record.target.parentElement);
          else {
            // Scan inserted content only, not the entire parent on every app update.
            for (const node of record.addedNodes) {
              if (node.nodeType === 1) this.queue(node);
              else if (node.nodeType === 3) this.queue(record.target);
            }
          }
        }
      });
      this.observe();
      queueMicrotask(() => {
        let saved;
        try { saved = localStorage.getItem(preferenceKey); } catch (_) {}
        if (saved && this.available(saved) && saved !== this.base) this.select(saved);
        else this.sync();
      });
      window.addEventListener('pagehide', () => {
        this.paused = true;
        this.observer.disconnect();
        this.pending.clear();
        if (this.frame) cancelAnimationFrame(this.frame);
        this.frame = null;
      });
      window.addEventListener('pageshow', () => {
        this.paused = false;
        this.observe();
        this.queue(document.body);
      });
    }
    observe() {
      // Base-currency prices already come from Shopify. Observing them wastes work
      // on every review, slider and app mutation without changing any prices.
      if (this.paused || this.accountPage || this.selected === this.base) return;
      this.observer.observe(document.body, { childList: true, subtree: true, characterData: true });
    }
    available(code) {
      return /^[A-Z]{3}$/.test(code) && !!document.querySelector('[data-mip-currency="' + code + '"]');
    }
    queue(node) {
      if (this.paused || this.accountPage || this.selected === this.base || !node || node.nodeType !== 1 || node.closest(ignoredSelector)) return;
      for (const root of this.pending) {
        if (root.contains(node)) return;
        if (node.contains(root)) this.pending.delete(root);
      }
      this.pending.add(node);
      if (this.frame) return;
      this.frame = requestAnimationFrame(() => {
        this.frame = null;
        const roots = [...this.pending];
        this.pending.clear();
        this.paint(roots);
      });
    }
    async loadRates() {
      if (this.rates) return;
      if (!this.rateRequest) {
        this.rateRequest = (async () => {
          const abort = new AbortController();
          const timeout = setTimeout(() => abort.abort(), 8000);
          try {
            const response = await fetch('https://cdn.shopify.com/s/javascripts/currencies.js', {
              signal: abort.signal, credentials: 'omit', referrerPolicy: 'no-referrer'
            });
            if (!response.ok) throw new Error('rates');
            const source = await response.text();
            const block = source.match(/\brates\s*:\s*\{([^{}]+)\}/);
            if (!block) throw new Error('rates');
            const rates = Object.create(null);
            for (const entry of block[1].split(',')) {
              const pair = entry.trim().match(/^([A-Z]{3})\s*:\s*((?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?)$/i);
              if (!pair) throw new Error('rates');
              const rate = Number(pair[2]);
              if (!(rate > 0) || !Number.isFinite(rate)) throw new Error('rates');
              rates[pair[1]] = rate;
            }
            if (!rates[this.base]) throw new Error('rates');
            this.rates = rates;
          } finally { clearTimeout(timeout); }
        })().finally(() => { this.rateRequest = null; });
      }
      return this.rateRequest;
    }
    async select(code) {
      if (!this.available(code) || this.busy) return false;
      this.error = '';
      this.busy = true;
      this.sync();
      try {
        if (code !== this.base) {
          await this.loadRates();
          if (!this.rates[code]) throw new Error('rates');
        }
        this.selected = code;
        this.formatter = new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
          style: 'currency', currency: code, currencyDisplay: 'code'
        });
        try { localStorage.setItem(preferenceKey, code); } catch (_) {}
        this.paint([document.body]);
        return true;
      } catch (_) {
        this.error = this.config.error.replace('[currency]', this.selected);
        return false;
      } finally {
        this.busy = false;
        this.sync();
      }
    }
    convertText(text) {
      if (this.selected === this.base) return text;
      return text.replace(this.moneyPattern, (match, before, after) => {
        const value = parseMoneyAmount(before || after, this.fractionDigits);
        return Number.isFinite(value) ? this.formatter.format(value * this.rates[this.base] / this.rates[this.selected]) : match;
      });
    }
    paint(roots) {
      this.observer.disconnect();
      try {
        if (!this.accountPage) {
          const parents = new Set();
          for (const root of roots) {
            if (!root.isConnected) continue;
            const matches = root.matches(priceSelector) ? [root] : [];
            matches.push(...root.querySelectorAll(priceSelector));
            const containing = root.closest(priceSelector);
            if (containing) matches.push(containing);
            for (const element of matches) {
              if (element.closest(ignoredSelector)) continue;
              const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT);
              let node;
              while ((node = walker.nextNode())) {
                if (!node.parentElement.closest(ignoredSelector)) parents.add(node.parentElement);
              }
            }
          }
          for (const parent of parents) {
            let previous = [];
            try { previous = JSON.parse(parent.dataset.mipMoneySnapshot || '[]'); } catch (_) {}
            const snapshot = [];
            [...parent.childNodes].forEach((node, index) => {
              if (node.nodeType !== 3) return;
              const prior = Array.isArray(previous) && previous.find(item => item.index === index && item.last === node.nodeValue);
              const original = prior ? prior.original : node.nodeValue;
              if (typeof original !== 'string') return;
              const rendered = this.convertText(original);
              if (rendered !== original || prior) {
                snapshot.push({ index, original, last: rendered });
                if (node.nodeValue !== rendered) node.nodeValue = rendered;
              }
            });
            if (snapshot.length) parent.dataset.mipMoneySnapshot = JSON.stringify(snapshot);
            else delete parent.dataset.mipMoneySnapshot;
          }
        }
        this.notices();
      } finally { this.observe(); }
    }
    noticeText() { return this.config.notice.replace('[currency]', this.selected); }
    notices() {
      const active = this.selected !== this.base && !this.accountPage;
      let banner = document.getElementById('MipDisplayCurrencyNotice');
      const main = document.getElementById('MainContent');
      if (!banner && main) {
        banner = document.createElement('p');
        banner.id = 'MipDisplayCurrencyNotice';
        banner.className = 'mip-display-notice';
        banner.setAttribute('role', 'status');
        main.before(banner);
      }
      if (banner) {
        banner.hidden = !active && !this.error;
        const filterNote = location.pathname.includes('/collections/') ? ' ' + this.config.filterNote.replace('[currency]', this.base) : '';
        const text = this.error || (this.noticeText() + filterNote);
        if (banner.textContent !== text) banner.textContent = text;
      }
      document.querySelectorAll('.button-checkout,button[name="checkout"]').forEach(button => {
        let note = button.previousElementSibling;
        if (!note || !note.classList.contains('mip-conversion-note')) {
          note = document.createElement('p');
          note.className = 'mip-conversion-note';
          button.before(note);
        }
        note.hidden = !active;
        if (note.textContent !== this.noticeText()) note.textContent = this.noticeText();
      });
    }
    sync() {
      document.querySelectorAll('mip-header-currency').forEach(menu => {
        const trigger = menu.querySelector('.mip-region-trigger');
        trigger.disabled = false;
        trigger.setAttribute('aria-busy', String(!!this.busy));
        trigger.setAttribute('aria-label', this.config.label + ': ' + this.selected);
        menu.querySelector('[data-mip-currency-label]').textContent = this.selected;
        menu.querySelectorAll('[data-mip-currency]').forEach(button => {
          button.disabled = !!this.busy;
          button.setAttribute('aria-pressed', String(button.dataset.mipCurrency === this.selected));
        });
        const status = menu.querySelector('[data-mip-currency-status]');
        if (!status.dataset.defaultText) status.dataset.defaultText = status.textContent;
        status.textContent = this.busy ? this.config.loading : (this.error || status.dataset.defaultText);
      });
      this.observer.disconnect();
      this.notices();
      this.observe();
    }
  }

  class MipHeaderCurrency extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.trigger = this.querySelector('.mip-region-trigger');
      this.panel = this.querySelector('.mip-region-panel');
      if (!this.trigger || !this.panel) return;
      this.bound = true;
      this.events = new AbortController();
      if (!converter) converter = new DisplayCurrency(this.dataset);
      const on = (node, event, handler, options = {}) => node.addEventListener(event, handler, { ...options, signal: this.events.signal });
      on(this.panel, 'click', async event => {
        const choice = event.target.closest('[data-mip-currency]');
        if (!choice) return;
        if (await converter.select(choice.dataset.mipCurrency)) {
          this.close();
          this.trigger.focus({ preventScroll: true });
        }
      });
      on(this.panel, 'toggle', event => {
        this.trigger.setAttribute('aria-expanded', String(event.newState === 'open'));
        if (event.newState === 'open') { this.position(); this.focusSelected(); }
      });
      on(this.trigger, 'click', () => {
        if (this.panel.showPopover) requestAnimationFrame(() => { if (this.isOpen()) this.position(); });
        else this.isOpen() ? this.close() : this.openFallback();
      });
      on(this.panel, 'keydown', event => {
        if (!['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) return;
        const options = [...this.panel.querySelectorAll('[data-mip-currency]:not(:disabled)')];
        if (!options.length) return;
        event.preventDefault();
        const index = options.indexOf(document.activeElement);
        const next = event.key === 'Home' ? 0 : event.key === 'End' ? options.length - 1 : (index + (event.key === 'ArrowDown' ? 1 : -1) + options.length) % options.length;
        options[next].focus();
      });
      on(window, 'resize', () => { if (this.isOpen()) this.position(); });
      on(window, 'scroll', () => { if (this.isOpen()) this.close(); }, { passive: true });
      on(document, 'keydown', event => {
        if (event.key === 'Escape' && this.isOpen()) { this.close(); this.trigger.focus({ preventScroll: true }); }
      });
      on(document, 'pointerdown', event => { if (!this.contains(event.target)) this.close(); });
      converter.sync();
    }
    focusSelected() { this.panel.querySelector('[aria-pressed="true"]')?.focus({ preventScroll: true }); }
    isOpen() { return this.panel.hasAttribute('data-mip-open') || (this.panel.showPopover && this.panel.matches(':popover-open')); }
    openFallback() { this.panel.setAttribute('data-mip-open', ''); this.trigger.setAttribute('aria-expanded', 'true'); this.position(); this.focusSelected(); }
    close() {
      if (this.panel.hidePopover && this.panel.matches(':popover-open')) this.panel.hidePopover();
      this.panel.removeAttribute('data-mip-open');
      this.trigger.setAttribute('aria-expanded', 'false');
    }
    position() {
      const rect = this.trigger.getBoundingClientRect();
      const width = Math.min(176, innerWidth - 24);
      const below = Math.max(0, innerHeight - rect.bottom - 20);
      const above = Math.max(0, rect.top - 20);
      const openAbove = below < 200 && above > below;
      const height = Math.min(360, openAbove ? above : below);
      this.panel.style.width = width + 'px';
      this.panel.style.left = Math.max(12, Math.min(innerWidth - width - 12, rect.right - width)) + 'px';
      this.panel.style.maxHeight = height + 'px';
      this.panel.style.top = Math.max(12, openAbove ? rect.top - this.panel.getBoundingClientRect().height - 8 : rect.bottom + 8) + 'px';
    }
    disconnectedCallback() { if (this.events) this.events.abort(); this.bound = false; }
  }
  customElements.define('mip-header-currency', MipHeaderCurrency);
})();
